<template>
  <section data-testid="device-uptime-chart" class="flex flex-col gap-6">
    <div class="flex flex-wrap gap-3">
      <div data-testid="device-uptime-month" class="flex-1 px-4 py-3 rounded-lg shadow-md min-w-[14rem] state_unknown">
        <p class="text-sm">{{ $t("uptimeHistory.monthToDate", { month: monthName }) }}</p>
        <p class="mt-1 text-2xl font-semibold" data-testid="device-uptime-month-hours">
          {{ month ? `${formatNumber(month.hours)} h` : "-" }}
        </p>
        <p
          v-if="month"
          class="flex items-center gap-1 mt-1 text-sm text-gray-600"
          data-testid="device-uptime-month-threshold"
        >
          <CheckCircleIcon v-if="month.reached" class="w-4 h-4 text-green-600" aria-hidden="true" />
          {{
            $t(month.reached ? "uptimeHistory.thresholdReached" : "uptimeHistory.thresholdNotReached", {
              month: monthName,
              share: formatNumber(month.thresholdShare),
              remaining: formatNumber(month.remainingHours),
            })
          }}
        </p>
      </div>
      <div
        data-testid="device-uptime-average"
        class="flex-1 px-4 py-3 rounded-lg shadow-md min-w-[14rem] state_unknown"
      >
        <p class="text-sm">{{ $t("uptimeHistory.average", { days }) }}</p>
        <p class="mt-1 text-2xl font-semibold" data-testid="device-uptime-average-percent">
          {{ average === null ? "-" : `${formatNumber(average)} %` }}
        </p>
        <p class="mt-1 text-sm text-gray-600">{{ $t("uptimeHistory.averageHint") }}</p>
      </div>
    </div>

    <div>
      <h3 class="text-base font-medium text-gray-900">{{ $t("uptimeHistory.title") }}</h3>
      <p class="text-sm text-gray-500">{{ $t("uptimeHistory.subtitle", { days }) }}</p>
    </div>

    <div v-if="loading" data-testid="device-uptime-chart-loading" class="py-16 text-center">
      <div
        class="inline-block w-6 h-6 border-4 border-blue-500 border-solid rounded-full animate-spin border-r-transparent"
      ></div>
    </div>
    <p v-else-if="!uptime" data-testid="device-uptime-chart-error" class="py-16 text-sm text-center text-gray-500">
      {{ $t("uptimeHistory.loadError") }}
    </p>
    <p v-else-if="!hasUptime" data-testid="device-uptime-chart-empty" class="py-16 text-sm text-center text-gray-500">
      {{ $t("uptimeHistory.noData", { days }) }}
    </p>
    <div v-else class="relative h-72">
      <canvas ref="canvas" data-testid="device-uptime-chart-canvas" role="img" :aria-label="ariaLabel"></canvas>
    </div>

    <table v-if="hasUptime && !loading" class="sr-only">
      <caption>
        {{
          $t("uptimeHistory.title")
        }}
      </caption>
      <thead>
        <tr>
          <th scope="col">{{ $t("uptimeHistory.date") }}</th>
          <th scope="col">{{ $t("uptimeHistory.hours") }}</th>
          <th scope="col">{{ $t("uptimeHistory.share") }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="bar in bars" :key="bar.start.toISOString()">
          <td>{{ formatTooltipDate(bar.start) }}</td>
          <td>{{ formatNumber(bar.hours) }}</td>
          <td>{{ formatNumber(bar.percent) }}</td>
        </tr>
      </tbody>
    </table>
  </section>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { CheckCircleIcon } from "@heroicons/vue/solid";
import { BarController, BarElement, CategoryScale, Chart, LinearScale, Tooltip } from "chart.js";
import {
  averageUptimePercent,
  dailyUptimeBars,
  formatUptimeNumber,
  UPTIME_HISTORY_DAYS,
  utcMonthName,
} from "@/lib/uptime";

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip);

// Tailwind steps of the app's accent (indigo) and greys, so the chart matches the platform.
const COLORS = {
  bar: "#6366f1",
  barHover: "#4f46e5",
  barToday: "#a5b4fc",
  grid: "#e5e7eb",
  axisText: "#6b7280",
  tooltip: "#1f2937",
};

export default {
  name: "DeviceUptimeChart",
  components: { CheckCircleIcon },
  props: {
    // Device.uptime with daily buckets, or null when it could not be loaded.
    uptime: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    // Month to date (uptimeSummary), shared with the uptime pill on the device page.
    month: { type: Object, default: null },
  },
  setup(props) {
    const { t, locale } = useI18n();
    const canvas = ref(null);
    // Not reactive on purpose: Vue proxies break Chart.js internals.
    let chart = null;

    const days = UPTIME_HISTORY_DAYS;
    const bars = computed(() => dailyUptimeBars(props.uptime?.buckets));
    const hasUptime = computed(() => bars.value.some((bar) => bar.hours > 0));
    const average = computed(() => averageUptimePercent(props.uptime?.buckets));
    const monthName = computed(() => utcMonthName(locale.value));

    const formatNumber = (value) => formatUptimeNumber(value, locale.value);
    const formatLabel = (date) =>
      new Intl.DateTimeFormat(locale.value, { day: "numeric", month: "short", timeZone: "UTC" }).format(date);
    const formatTooltipDate = (date) =>
      new Intl.DateTimeFormat(locale.value, {
        weekday: "short",
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }).format(date);

    const ariaLabel = computed(() =>
      t("uptimeHistory.ariaLabel", { days, average: average.value === null ? "-" : formatNumber(average.value) })
    );

    const chartConfig = () => {
      const currentBars = bars.value;
      const fontFamily = canvas.value ? getComputedStyle(canvas.value).fontFamily : undefined;
      const font = { family: fontFamily || undefined, size: 12 };
      const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      return {
        type: "bar",
        data: {
          labels: currentBars.map((bar) => formatLabel(bar.start)),
          datasets: [
            {
              data: currentBars.map((bar) => bar.percent),
              backgroundColor: currentBars.map((bar) => (bar.partial ? COLORS.barToday : COLORS.bar)),
              hoverBackgroundColor: COLORS.barHover,
              borderRadius: 4,
              borderSkipped: "start",
              categoryPercentage: 1,
              barPercentage: 0.8,
              maxBarThickness: 24,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: reducedMotion ? false : { duration: 300 },
          // The whole day column is the hover target, not only the bar.
          interaction: { mode: "index", intersect: false },
          scales: {
            x: {
              grid: { display: false },
              border: { color: COLORS.grid },
              ticks: { color: COLORS.axisText, font, autoSkip: true, maxRotation: 0, autoSkipPadding: 16 },
            },
            y: {
              min: 0,
              max: 100,
              border: { display: false },
              grid: { color: COLORS.grid },
              ticks: { color: COLORS.axisText, font, stepSize: 25, callback: (value) => `${value} %` },
            },
          },
          plugins: {
            tooltip: {
              backgroundColor: COLORS.tooltip,
              padding: 10,
              cornerRadius: 8,
              displayColors: false,
              titleFont: { ...font, weight: "600" },
              bodyFont: font,
              callbacks: {
                title: (items) => formatTooltipDate(currentBars[items[0].dataIndex].start),
                label: (item) => {
                  const bar = currentBars[item.dataIndex];
                  const values = { hours: formatNumber(bar.hours), percent: formatNumber(bar.percent) };
                  return t(bar.partial ? "uptimeHistory.tooltipToday" : "uptimeHistory.tooltip", values);
                },
              },
            },
          },
        },
      };
    };

    const destroyChart = () => {
      if (chart) {
        chart.destroy();
        chart = null;
      }
    };

    const renderChart = () => {
      destroyChart();
      if (canvas.value && hasUptime.value) {
        chart = new Chart(canvas.value, chartConfig());
      }
    };

    onMounted(renderChart);
    // flush "post": the canvas only exists after the DOM shows the loaded data.
    watch([bars, locale], renderChart, { flush: "post" });
    onBeforeUnmount(destroyChart);

    return {
      canvas,
      days,
      bars,
      hasUptime,
      average,
      monthName,
      ariaLabel,
      formatNumber,
      formatTooltipDate,
    };
  },
};
</script>
